import React, { useCallback } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CustomDatePicker from '../../../../Components/Utility/DatePicker/DatePicker';
import { usePOCreation } from '../contextProvider/POCreationContext';

export default function ControlRendererForPO({ control, value, onChange, disabled = false, isPreviewMode = false, uniqueKey }) {
  const { t } = useTranslation('purchase-order');
  const { creationState, clearValidationError, isFieldsDisabledByStatus } = usePOCreation();

  const isSupplierLoggedIn = useSelector((state) => state.UserDetails?.isSupplierLoggedIn);

  const fieldKey = uniqueKey || control?.id;
  const validationError = creationState.validationErrors?.[fieldKey];

  /**
   * Handle input change with validation
   * @param {any} newValue - New input value
   */
  const handleInputChange = useCallback(
    (newValue) => {
      onChange(newValue);
      if (validationError) {
        clearValidationError(fieldKey);
      }
    },
    [onChange, validationError, fieldKey, clearValidationError],
  );

  /**
   * Handle checkbox change
   * @param {Event} e - Checkbox change event
   * @param {Object} opt - Checkbox option
   */
  const handleCheckboxChange = useCallback(
    (e, opt) => {
      const currentValue = Array.isArray(value) ? value : [];
      if (e.target.checked) {
        handleInputChange([...currentValue, opt.value || opt.name]);
      } else {
        handleInputChange(currentValue.filter((v) => v !== (opt.value || opt.name)));
      }
    },
    [value, handleInputChange],
  );

  /**
   * Handle radio button change
   * @param {Event} e - Radio button change event
   */
  const handleRadioChange = useCallback(
    (e) => {
      handleInputChange(e.target.value);
    },
    [handleInputChange],
  );

  /**
   * Handle text input change
   * @param {Event} e - Text input change event
   */
  const handleTextChange = useCallback(
    (e) => {
      handleInputChange(e.target.value);
    },
    [handleInputChange],
  );

  /**
   * Handle select change
   * @param {Object} selectedOption - Selected option
   */
  const handleSelectChange = useCallback(
    (selectedOption) => {
      handleInputChange(selectedOption);
    },
    [handleInputChange],
  );

  /**
   * Handle date change
   * @param {Date} date - Selected date
   */
  const handleDateChange = useCallback(
    (date) => {
      handleInputChange(date);
    },
    [handleInputChange],
  );

  const optionMatchesValue = (opt, currentValue) =>
    opt?._id === currentValue || opt?.id === currentValue || opt?.value === currentValue || opt?.name === currentValue || opt?.label === currentValue;

  const resolveDropdownSelectedOption = (options, currentValue) => {
    if (!options || options.length === 0 || !currentValue) return null;
    if (typeof currentValue === 'object') return currentValue;
    return options.find((opt) => optionMatchesValue(opt, currentValue)) || null;
  };

  if (control.enable === false) {
    return (
      <div className="control-disabled">
        <span className="text-muted">{control.label || t('CREATE_EDIT_PO.CONTROL_DISABLED')}</span>
      </div>
    );
  }

  const isRequired = control.required === true;

  const renderLabel = () => {
    if (!control.label) return null;

    return (
      <p className="field-name">
        <span className={control?.isBold ? 'font-heavy' : 'font-bold'}>{control.label}</span>
        {isRequired && <span className="asterisk text-danger">*</span>}
      </p>
    );
  };

  const renderTextbox = () => {
    const inputName = uniqueKey || control.id || `textbox_${Date.now()}`;
    return (
      <div className="common-input-control mb-3">
        {renderLabel()}
        <div className="d-flex">
          <input
            type="text"
            placeholder={control?.placeholder || t('CREATE_EDIT_PO.ENTER_TEXT')}
            name={inputName}
            id={inputName}
            value={value || ''}
            onChange={handleTextChange}
            className="form-control"
            disabled={disabled || isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
            readOnly={isPreviewMode}
            maxLength={control?.characterLimit}
          />
          {control?.characterLimit && (
            <div className="text-count p-2">
              {(value || '').length}/{control?.characterLimit}
            </div>
          )}
        </div>
        {validationError && <div className="invalid-feedback d-block">{validationError}</div>}
      </div>
    );
  };

  const renderTextarea = () => {
    const inputName = uniqueKey || control.id || `textarea_${Date.now()}`;
    return (
      <div className="common-input-control mb-3">
        {renderLabel()}
        <div className="d-flex">
          {isPreviewMode ? (
            <div className="form-control-plaintext preview-textarea">{value || '--'}</div>
          ) : (
            <textarea
              placeholder={control?.placeholder || t('CREATE_EDIT_PO.ENTER_TEXT')}
              name={inputName}
              id={inputName}
              value={value || ''}
              onChange={handleTextChange}
              className="form-control h-auto resize-none"
              disabled={disabled || isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
              rows={control.rows || 3}
            />
          )}
        </div>
        {validationError && <div className="invalid-feedback d-block">{validationError}</div>}
      </div>
    );
  };

  const renderDropdown = () => (
    <div className="common-react-select mb-3">
      {renderLabel()}
      <Select
        options={control?.options}
        getOptionLabel={(option) => option?.name || option?.label || String(option?.value ?? '')}
        getOptionValue={(option) => option?._id || option?.id}
        className="common-select"
        classNamePrefix="common-select"
        menuPosition="fixed"
        placeholder={control?.placeholder || t('COMMON_LABEL.SELECT', 'Select')}
        value={resolveDropdownSelectedOption(control?.options, value)}
        isClearable
        maxMenuHeight={150}
        onChange={handleSelectChange}
        isDisabled={disabled || isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
      />
      {validationError && <div className="invalid-feedback d-block">{validationError}</div>}
    </div>
  );

  const renderControl = () => {
    switch (control.type) {
      case 'Textbox':
        return renderTextbox();

      case 'TextArea':
        return renderTextarea();

      case 'Dropdown':
        return renderDropdown();

      case 'DatePicker':
        return (
          <div className="common-input-control mb-3">
            {renderLabel()}
            <CustomDatePicker
              keyProp={control?.label}
              dateFormat="dd-MMM-yyyy"
              valueProp={value || null}
              name={control?.label}
              placeholder={control?.placeholder}
              disable={disabled || isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
              onChange={handleDateChange}
            />
            {validationError && <div className="invalid-feedback d-block">{validationError}</div>}
          </div>
        );

      case 'Checkbox':
        return (
          <div className="w-100 mb-3">
            {renderLabel()}
            <div className="checkbox-options">
              {control?.options?.map((opt, ind) => {
                const timeStamp = `${new Date().valueOf()}_${ind}`;
                const checkboxId = `Checkbox_${timeStamp}_${control.id}_${opt?.id || ind}`;
                return (
                  <div className="common-checkbox mb-2" key={checkboxId}>
                    <input
                      type="checkbox"
                      id={checkboxId}
                      name={`Checkbox_${control.id}`}
                      value={opt.value || opt.name}
                      onChange={(e) => handleCheckboxChange(e, opt)}
                      checked={Array.isArray(value) ? value.includes(opt.value || opt.name) : false}
                      disabled={disabled || isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
                    />
                    <label htmlFor={checkboxId} className="chklabel">
                      <span>{opt?.name || opt?.label}</span>
                    </label>
                  </div>
                );
              })}
            </div>
            {validationError && <div className="text-danger small mt-1">{validationError}</div>}
          </div>
        );

      case 'RadioButton':
        return (
          <div className="w-100 mb-3">
            {renderLabel()}
            <div className="d-flex align-items-center flex-wrap">
              {control.options?.map((opt, i) => {
                const baseId = uniqueKey || control.id;
                const radioId = `radio_${baseId}_${opt?.id || opt?.value || opt?.name || i}`;
                const groupName = `radio_group_${baseId}`;
                return (
                  <p className="custom-radio mr-4 mb-2" key={radioId}>
                    <input
                      type="radio"
                      id={radioId}
                      name={groupName}
                      value={opt.value || opt.name}
                      onChange={handleRadioChange}
                      checked={value === (opt.value || opt.name)}
                      disabled={disabled || isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
                    />
                    <label htmlFor={radioId}>{opt?.name || opt?.label}</label>
                  </p>
                );
              })}
            </div>
            {validationError && <div className="text-danger small mt-1">{validationError}</div>}
          </div>
        );

      case 'ImageUpload':
        // Image upload is disabled for PO creation
        return (
          <div className="w-100 mb-3">
            {renderLabel()}
            <div className="text-muted">{t('CREATE_EDIT_PO.IMAGE_UPLOAD_DISABLED')}</div>
          </div>
        );

      case 'TableTitle':
        return (
          <div className="w-100 mb-3">
            <h4 className={control.isBold ? 'font-weight-bold' : 'font-weight-normal'}>{control.label || t('CREATE_EDIT_PO.TABLE_TITLE')}</h4>
          </div>
        );

      default:
        return (
          <div className="w-100 mb-3">
            <span className="text-muted">{control.label || t('CREATE_EDIT_PO.UNKNOWN_CONTROL_TYPE')}</span>
          </div>
        );
    }
  };

  return <div className={`${disabled ? 'control-disabled' : ''}`}>{renderControl()}</div>;
}

ControlRendererForPO.displayName = 'ControlRendererForPO';
